import React, { useState, useEffect, useCallback, useRef } from 'react';
import AlbumCard from './components/AlbumCard';
import AlbumForm from './components/AlbumForm';
import AlbumSortPopup from './components/AlbumSortPopup';
import AlbumFilterPopup from './components/AlbumFilterPopup';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import Pagination from '../../components/shared/Pagination';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import EmptyState from '../../components/shared/EmptyState';
import { toast } from '../../components/shared/toast';
import { apiClient } from '../../api/apiClient';
import { 
  GET_ALBUMS_URL, 
  CREATE_ALBUM_URL, 
  UPDATE_ALBUM_URL, 
  DELETE_ALBUM_URL,
  GET_RECORD_LABEL_DROPDOWN_URL
} from '../../api/album';
import en from '../../translations/en.json';
import { buildAlbumsExportCsv, downloadCsv } from './albumsExport';
import './Albums.css';

function Albums({ onSelectAlbum, onSelectSong }) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateAlbums = canCreate('albums');
  const canEditAlbums = canEdit('albums');
  const canDeleteAlbums = canDelete('albums');

  const [albums, setAlbums] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState('az'); // az, za, newest, oldest
  const [filters, setFilters] = useState({ labelId: null, trackCount: null });
  
  const [recordLabels, setRecordLabels] = useState([]);

  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [isAddingAlbum, setIsAddingAlbum] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const lastAlbumsRequestRef = useRef('');

  // Confirmation Modals State
  const [pendingDeleteAlbum, setPendingDeleteAlbum] = useState(null);
  const [showPrimaryDeleteModal, setShowPrimaryDeleteModal] = useState(false);
  const [showSecondaryDeleteModal, setShowSecondaryDeleteModal] = useState(false);
  const [dependentSongs, setDependentSongs] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  // Fetch record labels for filter popup
  const fetchFilterLabels = useCallback(async () => {
    try {
      const res = await apiClient(`${GET_RECORD_LABEL_DROPDOWN_URL}?limit=100`);
      if (res && res.labels) {
        setRecordLabels(res.labels);
      }
    } catch (err) {
      console.error('Failed to fetch filter record labels:', err);
    }
  }, []);

  useEffect(() => {
    fetchFilterLabels();
  }, []);

  // Fetch albums from backend
  const fetchAlbums = useCallback(async () => {
    const requestSignature = `${currentPage}|${searchQuery.trim()}|${selectedSort}|${filters.labelId ?? ''}|${filters.trackCount ?? ''}`;
    if (lastAlbumsRequestRef.current === requestSignature) {
      return;
    }

    lastAlbumsRequestRef.current = requestSignature;

    try {
      setIsLoading(true);
      let queryParams = [`page=${currentPage}`, 'limit=20'];

      if (searchQuery.trim()) {
        queryParams.push(`search=${encodeURIComponent(searchQuery.trim())}`);
      }
      if (selectedSort) {
        queryParams.push(`sortBy=${selectedSort}`);
      }
      if (filters.labelId) {
        queryParams.push(`labelId=${filters.labelId}`);
      }
      if (filters.trackCount !== null && filters.trackCount !== undefined) {
        queryParams.push(`trackCount=${filters.trackCount}`);
      }

      const url = `${GET_ALBUMS_URL}?${queryParams.join('&')}`;
      const response = await apiClient(url);

      if (response) {
        setAlbums(response.albums || response.data || []);
        setTotalPages(response.totalPages || 1);
        setTotalRecords(response.totalRecords || response.totalCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch albums:', err);
      toast.error('Failed to load albums from backend.');
      lastAlbumsRequestRef.current = '';
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, selectedSort, filters]);

  useEffect(() => {
    fetchAlbums();
  }, [currentPage, searchQuery, selectedSort, filters]);

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSortSelect = (sortId) => {
    setSelectedSort(sortId);
    setSortOpen(false);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ labelId: null, trackCount: null });
    setFilterOpen(false);
    setCurrentPage(1);
  };

  // CSV/Excel Export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_ALBUMS_URL}?page=1&limit=1000`);
      const list = response?.albums || response?.data || [];

      if (list.length > 0) {
        const csvContent = buildAlbumsExportCsv(list);
        downloadCsv(csvContent, `albums_export_${new Date().toISOString().slice(0, 10)}.csv`);
        toast.success('Albums exported successfully!');
      } else {
        toast.error('No albums available to export.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export albums.');
    } finally {
      setIsExporting(false);
    }
  };

  // ─── SAVE CREATE / EDIT ───
  const handleSaveForm = async (formData) => {
    try {
      if (editingAlbum) {
        const response = await apiClient(UPDATE_ALBUM_URL(editingAlbum.id), {
          method: 'PUT',
          body: formData
        });
        if (response) {
          toast.success('Album updated successfully');
          setEditingAlbum(null);
          fetchAlbums();
        }
      } else {
        const response = await apiClient(CREATE_ALBUM_URL, {
          method: 'POST',
          body: formData
        });
        if (response) {
          toast.success('Album created successfully');
          setIsAddingAlbum(false);
          fetchAlbums();
        }
      }
    } catch (err) {
      console.error('Error saving album:', err);
      toast.warning(err.message || 'Album already exists.');
    }
  };

  // ─── DELETE FLOW ───
  const handleDeleteClick = (album) => {
    setPendingDeleteAlbum(album);
    setShowPrimaryDeleteModal(true);
  };

  const handleConfirmPrimaryDelete = async () => {
    if (!pendingDeleteAlbum) return;
    setShowPrimaryDeleteModal(false);

    try {
      const response = await apiClient(DELETE_ALBUM_URL(pendingDeleteAlbum.id), {
        method: 'DELETE'
      });

      if (response && response.hasDependencies) {
        setDependentSongs(response.dependentSongs || []);
        setShowSecondaryDeleteModal(true);
      } else if (response && response.success !== false) {
        toast.success('Album deleted successfully');
        setPendingDeleteAlbum(null);
        fetchAlbums();
      }
    } catch (err) {
      console.error('Error deleting album:', err);
      toast.error(err.message || 'Failed to delete album.');
    }
  };

  const handleConfirmSecondaryDelete = async () => {
    if (!pendingDeleteAlbum) return;
    setShowSecondaryDeleteModal(false);

    try {
      const response = await apiClient(DELETE_ALBUM_URL(pendingDeleteAlbum.id), {
        method: 'DELETE',
        body: { force: true }
      });

      if (response && response.success !== false) {
        toast.success('Album deleted successfully');
        setPendingDeleteAlbum(null);
        setDependentSongs([]);
        fetchAlbums();
      }
    } catch (err) {
      console.error('Error force deleting album:', err);
      toast.error(err.message || 'Failed to delete album.');
    }
  };

  if (editingAlbum) {
    return (
      <AlbumForm 
        existingAlbum={editingAlbum}
        onSave={handleSaveForm}
        onCancel={() => setEditingAlbum(null)}
      />
    );
  }

  if (isAddingAlbum) {
    return (
      <AlbumForm 
        onSave={handleSaveForm}
        onCancel={() => setIsAddingAlbum(false)}
      />
    );
  }

  return (
    <ProtectedPage module="albums">
      <div className="albums-page">
      {/* Loading overlay animation for Export */}
      <LoadingOverlay isLoading={isExporting} message="Generating Albums Export..." />

      {/* Shared Header Controls */}
      <SharedHeader
        title=""
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t('albums.searchPlaceholder') || 'Search Albums'}
        resultCount={totalRecords}
        resultLabel={totalRecords === 1 ? 'Album' : 'Albums'}
        showSortFilter={true}
        onSortClick={() => {
          setSortOpen((prev) => !prev);
          setFilterOpen(false);
        }}
        onFilterClick={() => {
          setFilterOpen((prev) => !prev);
          setSortOpen(false);
        }}
        sortActive={sortOpen}
        filterActive={filterOpen}
        isSortApplied={selectedSort !== 'az'}
        isFilterApplied={filters.labelId !== null || filters.trackCount !== null}
        onExportClick={handleExport}
        onAddNewClick={canCreateAlbums ? () => setIsAddingAlbum(true) : null}
        addNewText={t('albums.addNew') || 'Add New Album'}
        canAddNew={canCreateAlbums}
      />

      {/* Popups rendered conditionally relative to header buttons */}
      <div className="popup-anchor-container" style={{ position: 'relative' }}>
        {sortOpen && (
          <AlbumSortPopup
            selectedSort={selectedSort}
            onSelect={handleSortSelect}
            onClose={() => setSortOpen(false)}
          />
        )}

        {filterOpen && (
          <AlbumFilterPopup
            filters={filters}
            recordLabels={recordLabels}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>

      {/* Album Cards Grid */}
      <div className="albums-grid-container" style={{ marginTop: '20px' }}>
        {isLoading ? (
          <LoadingOverlay isLoading message="Loading albums, please wait." />
        ) : albums.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', width: '100%' }}>
            <EmptyState
              hasSearch={!!(searchQuery || filters.labelId || filters.trackCount)}
              title={!!(searchQuery || filters.labelId || filters.trackCount) ? 'No albums found' : 'No albums yet'}
              subtitle={!!(searchQuery || filters.labelId || filters.trackCount)
                ? 'Try adjusting your search query or filter options.'
                : 'Add your first album to get started managing albums.'}
            />
          </div>
        ) : (
          albums.map((album) => (
            <AlbumCard
              key={album.id}
              album={album}
              onSelect={onSelectAlbum}
              onEdit={canEditAlbums ? (albumToEdit) => setEditingAlbum(albumToEdit) : null}
              onDelete={canDeleteAlbums ? handleDeleteClick : null}
              t={t}
            />
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!isLoading && albums.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalRecords}
          onPageChange={(page) => setCurrentPage(page)}
          t={t}
        />
      )}

      {/* Primary Delete Confirmation Modal */}
      {showPrimaryDeleteModal && (
        <ConfirmationModal
          title="Delete Album"
          message={`Are you sure you want to delete "${pendingDeleteAlbum?.name}"?`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmPrimaryDelete}
          onCancel={() => {
            setShowPrimaryDeleteModal(false);
            setPendingDeleteAlbum(null);
          }}
        />
      )}

      {/* Secondary Delete Confirmation Modal (Active Songs Dependency Warning) */}
      {showSecondaryDeleteModal && (
        <div 
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div 
            className="modal-content"
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '480px',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ef4444', marginBottom: '12px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Active Songs Relationship Warning</h3>
            </div>

            <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5', margin: '0 0 16px 0' }}>
              The album <strong>"{pendingDeleteAlbum?.name}"</strong> contains active song relationships. Deleting this album will remove these relationships.
            </p>

            {/* List of Affected Songs */}
            <div style={{ maxHeight: '160px', overflowY: 'auto', background: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
              {dependentSongs.map((song) => (
                <div key={song.id} style={{ fontSize: '13px', color: '#0f172a', fontWeight: '500', padding: '4px 0' }}>
                  • {song.name} ({song.artist})
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowSecondaryDeleteModal(false);
                  setPendingDeleteAlbum(null);
                  setDependentSongs([]);
                }}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSecondaryDelete}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#ef4444',
                  color: '#ffffff',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Force Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </ProtectedPage>
  );
}

export default Albums;
